import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Modal,
  Row,
  Spinner,
  Table,
  Pagination,
} from 'react-bootstrap';
import { toast } from 'react-hot-toast';
import {
  adminService,
  CareerMissionAdmin,
  CreateCareerMissionAdmin,
  QuizCompactAdminDto,
  Question,
  AdminAchievement,
  CareerMissionRequirementInput,
  CareerMissionQuestionGroupInput,
  UpdateCareerMissionAdmin,
} from '../services/api';
import { useSafeTranslation } from '../hooks/useSafeTranslation';

const REQ_TYPES: { value: string; label: string }[] = [
  { value: 'PERFECT_WINS_COUNT', label: 'Perfect wins (target count)' },
  { value: 'QUIZ_PLAY_COUNT', label: 'Play count (sessions)' },
  { value: 'SPECIFIC_QUESTIONS_CORRECT_ONCE', label: 'Specific questions (correct once each)' },
];

type QuestionGroupRow = { label: string; questionIds: string[] };

type ReqRow = {
  clientId: string;
  sortOrder: number;
  requirementType: string;
  targetCount: number | '';
  questionIds: string[];
  /** MC + specific questions: partition of questionIds */
  questionGroups: QuestionGroupRow[];
};

let reqIdSeq = 0;
function nextReqId() {
  reqIdSeq += 1;
  return `req-${reqIdSeq}`;
}

function looksLikeRequirementGuid(s: string): boolean {
  return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(s);
}

function emptyReqRow(sortOrder: number): ReqRow {
  return {
    clientId: nextReqId(),
    sortOrder,
    requirementType: 'PERFECT_WINS_COUNT',
    targetCount: 1,
    questionIds: [],
    questionGroups: [],
  };
}

function pruneGroupsForQuestionIds(groups: QuestionGroupRow[], questionIds: string[]): QuestionGroupRow[] {
  const allowed = new Set(questionIds);
  return groups
    .map((g) => ({
      ...g,
      questionIds: g.questionIds.filter((id) => allowed.has(id)),
    }))
    .filter((g) => g.questionIds.length > 0);
}

function missionToRows(m: CareerMissionAdmin): ReqRow[] {
  return m.requirements.map((r, i) => ({
    clientId: r.id || nextReqId(),
    sortOrder: r.sortOrder ?? i,
    requirementType: r.requirementType,
    targetCount:
      r.targetCount != null && r.requirementType !== 'SPECIFIC_QUESTIONS_CORRECT_ONCE'
        ? r.targetCount
        : '',
    questionIds: r.questionIds?.map(String) ?? [],
    questionGroups: (r.questionGroups ?? []).map((g) => ({
      label: g.label ?? '',
      questionIds: (g.questionIds ?? []).map(String),
    })),
  }));
}

const MAX_SOURCE_QUESTION_LABEL_LEN = 120;

/** Pergunta + resposta correta para distinguir itens com o mesmo enunciado (ex.: WITC). */
function sourceQuizQuestionCheckboxLabel(q: Question): string {
  const fromWitc = q.correctAnswerPt?.trim() || q.correctAnswerEn?.trim();
  const fromAnswers = q.answers?.find((a) => a.isCorrect)?.text?.trim();
  const correct = fromWitc || fromAnswers || '';
  const stem = (q.text ?? '').trim();
  const full = correct ? (stem ? `${stem} - ${correct}` : correct) : stem;
  if (full.length <= MAX_SOURCE_QUESTION_LABEL_LEN) return full;
  return `${full.slice(0, MAX_SOURCE_QUESTION_LABEL_LEN)}…`;
}

/** Nome do quiz no admin (título + tema opcional), alinhado à lista de quizzes. */
function quizAdminDisplayLine(q: { title: string; theme?: string | null }): string {
  const title = (q.title || '').trim();
  const theme = (q.theme || '').trim();
  if (theme) return `${title} — ${theme}`;
  return title;
}

/** Busca in-memory no cadastro de missão (quiz + texto da pergunta + apenas respostas corretas). */
function buildCareerQuestionSearchHaystack(q: Question, sourceQuiz?: QuizCompactAdminDto): string {
  const parts: string[] = [];
  if (sourceQuiz) {
    parts.push(
      sourceQuiz.title || '',
      sourceQuiz.titleEn || '',
      sourceQuiz.theme || '',
      sourceQuiz.themeEn || ''
    );
  }
  parts.push(
    q.text || '',
    q.textEn || '',
    q.correctAnswerPt || '',
    q.correctAnswerEn || '',
    q.correctAnswerEasyPt || '',
    q.correctAnswerEasyEn || '',
    q.correctAnswerMediumPt || '',
    q.correctAnswerMediumEn || ''
  );
  if (q.answers?.length) {
    for (const a of q.answers) {
      const isAnyCorrect = a.isCorrect || a.isCorrectEasy || a.isCorrectMedium;
      if (!isAnyCorrect) continue;
      if (a.text) parts.push(a.text);
      if (a.textEn) parts.push(a.textEn);
    }
  }
  return parts.join(' ').toLowerCase();
}

function filterCareerAdminQuestions(
  questions: Question[],
  query: string,
  sourceQuiz?: QuizCompactAdminDto
): Question[] {
  const t = query.trim().toLowerCase();
  if (!t) return questions;
  return questions.filter((q) => buildCareerQuestionSearchHaystack(q, sourceQuiz).includes(t));
}

const REWARD_PLAY_MEDIUM = 'play:medium';
const REWARD_PLAY_BOTH = 'play:both';

const AdminCareerMissions: React.FC = () => {
  const { t } = useSafeTranslation();
  const [items, setItems] = useState<CareerMissionAdmin[]>([]);
  const [quizzes, setQuizzes] = useState<QuizCompactAdminDto[]>([]);
  const [achievements, setAchievements] = useState<AdminAchievement[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const CAREER_MISSIONS_ADMIN_PAGE_SIZE = 50;
  const [missionsPage, setMissionsPage] = useState(1);
  const [missionsTotalPages, setMissionsTotalPages] = useState(1);
  const [missionsTotalCount, setMissionsTotalCount] = useState(0);
  const [missionsHasNext, setMissionsHasNext] = useState(false);
  const [missionsHasPrev, setMissionsHasPrev] = useState(false);

  const [showCreate, setShowCreate] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState<CareerMissionAdmin | null>(null);

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [titleEn, setTitleEn] = useState('');
  const [descriptionEn, setDescriptionEn] = useState('');
  const [sourceQuizId, setSourceQuizId] = useState('');
  const [rewardUnlockQuizId, setRewardUnlockQuizId] = useState('');
  const [rewardXp, setRewardXp] = useState(0);
  const [rewardAchievementId, setRewardAchievementId] = useState('');
  const [rewardUnlockPlayMedium, setRewardUnlockPlayMedium] = useState(false);
  const [rewardUnlockPlayHard, setRewardUnlockPlayHard] = useState(false);
  const [isActive, setIsActive] = useState(true);
  const [sortOrder, setSortOrder] = useState(0);
  const [reqRows, setReqRows] = useState<ReqRow[]>([emptyReqRow(0)]);
  const [sourceQuestions, setSourceQuestions] = useState<Question[]>([]);
  const [loadingQuestions, setLoadingQuestions] = useState(false);

  const [groupModalReqClientId, setGroupModalReqClientId] = useState<string | null>(null);
  const [groupModalLabel, setGroupModalLabel] = useState('');
  const [groupModalSelectedIds, setGroupModalSelectedIds] = useState<string[]>([]);
  const [groupModalQuestionSearch, setGroupModalQuestionSearch] = useState('');
  /** Por linha de requisito (clientId): filtro da lista de perguntas específicas */
  const [specificQuestionSearchByRow, setSpecificQuestionSearchByRow] = useState<Record<string, string>>(
    {}
  );

  const loadQuestionsForSource = useCallback(async (quizId: string) => {
    if (!quizId) {
      setSourceQuestions([]);
      return;
    }
    setLoadingQuestions(true);
    try {
      const all: Question[] = [];
      let page = 1;
      let more = true;
      while (more) {
        const res = await adminService.getQuizQuestions(quizId, {
          page,
          pageSize: 100,
        });
        all.push(...res.questions);
        more = res.hasNextPage;
        page += 1;
      }
      setSourceQuestions(all);
    } catch {
      setSourceQuestions([]);
      toast.error('Failed to load questions for quiz');
    } finally {
      setLoadingQuestions(false);
    }
  }, []);

  const loadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [missionsRes, qz, achRes] = await Promise.all([
        adminService.getCareerMissions({
          page: missionsPage,
          pageSize: CAREER_MISSIONS_ADMIN_PAGE_SIZE,
        }),
        adminService.getQuizCompacts(),
        adminService.getAchievements({ isActive: true, page: 1, pageSize: 500 }),
      ]);
      setItems(missionsRes.missions);
      setMissionsTotalCount(missionsRes.totalCount);
      setMissionsTotalPages(
        missionsRes.totalCount === 0 ? 1 : Math.max(1, missionsRes.totalPages)
      );
      setMissionsHasNext(missionsRes.hasNextPage);
      setMissionsHasPrev(missionsRes.hasPreviousPage);
      setQuizzes(qz);
      setAchievements(achRes.achievements);
      setError(null);
    } catch {
      setError('Failed to load career missions');
    } finally {
      setLoading(false);
    }
  }, [missionsPage]);

  useEffect(() => {
    void loadAll();
  }, [loadAll]);

  useEffect(() => {
    if (missionsTotalPages > 0 && missionsPage > missionsTotalPages) {
      setMissionsPage(missionsTotalPages);
    }
  }, [missionsTotalPages, missionsPage]);

  useEffect(() => {
    if (showCreate || showEdit) {
      void loadQuestionsForSource(sourceQuizId);
    }
  }, [sourceQuizId, showCreate, showEdit, loadQuestionsForSource]);

  useEffect(() => {
    setSpecificQuestionSearchByRow({});
  }, [sourceQuizId]);

  const sourceQuiz = useMemo(
    () => (sourceQuizId ? quizzes.find((q) => q.id === sourceQuizId) : undefined),
    [quizzes, sourceQuizId]
  );

  /** Backend QuizFormat: 0 = MultipleChoice, 1 = Witc */
  const isMcSourceQuiz = sourceQuiz?.format === 0;

  const unassignedQuestionIds = (row: ReqRow): string[] => {
    const inGroups = new Set(row.questionGroups.flatMap((g) => g.questionIds));
    return row.questionIds.filter((id) => !inGroups.has(id));
  };

  const groupModalRow = useMemo(
    () =>
      groupModalReqClientId
        ? reqRows.find((r) => r.clientId === groupModalReqClientId)
        : undefined,
    [groupModalReqClientId, reqRows]
  );

  const groupModalUnassignedQuestions = useMemo(() => {
    if (!groupModalRow || !groupModalReqClientId) return [];
    const ids = unassignedQuestionIds(groupModalRow);
    const qs = ids
      .map((id) => sourceQuestions.find((x) => x.id === id))
      .filter((q): q is Question => q != null);
    return filterCareerAdminQuestions(qs, groupModalQuestionSearch, sourceQuiz);
  }, [
    groupModalRow,
    groupModalReqClientId,
    sourceQuestions,
    groupModalQuestionSearch,
    sourceQuiz,
  ]);

  const commitGroupModal = () => {
    const label = groupModalLabel.trim();
    if (!groupModalReqClientId || !label) {
      toast.error(t('admin.careerGroupLabelRequired', 'Enter a group name.'));
      return;
    }
    if (groupModalSelectedIds.length === 0) {
      toast.error(
        t('admin.careerGroupPickOneQuestion', 'Select at least one question for this group.')
      );
      return;
    }
    const cid = groupModalReqClientId;
    setReqRows((rows) =>
      rows.map((r) => {
        if (r.clientId !== cid) return r;
        return {
          ...r,
          questionGroups: [
            ...r.questionGroups,
            { label, questionIds: [...groupModalSelectedIds] },
          ],
        };
      })
    );
    setGroupModalReqClientId(null);
    setGroupModalLabel('');
    setGroupModalSelectedIds([]);
  };

  const rewardUnlockSelectValue = (): string => {
    if (rewardUnlockQuizId) return `quiz:${rewardUnlockQuizId}`;
    if (rewardUnlockPlayMedium && rewardUnlockPlayHard) return REWARD_PLAY_BOTH;
    /** Missões antigas só com Hard: mesma recompensa que Médio+Difícil no backend. */
    if (rewardUnlockPlayHard) return REWARD_PLAY_BOTH;
    if (rewardUnlockPlayMedium) return REWARD_PLAY_MEDIUM;
    return '';
  };

  /** Ao mudar o quiz de origem, remove bandas solo inválidas para o novo quiz. */
  useEffect(() => {
    if (!sourceQuiz || rewardUnlockQuizId) return;
    let m = rewardUnlockPlayMedium;
    let h = rewardUnlockPlayHard;
    if (m && sourceQuiz.supportsPlayDifficultyMedium !== true) m = false;
    if (h && sourceQuiz.supportsPlayDifficultyHard !== true) h = false;
    if (m !== rewardUnlockPlayMedium) setRewardUnlockPlayMedium(m);
    if (h !== rewardUnlockPlayHard) setRewardUnlockPlayHard(h);
  }, [
    sourceQuizId,
    sourceQuiz,
    rewardUnlockQuizId,
    rewardUnlockPlayMedium,
    rewardUnlockPlayHard,
  ]);

  const applyRewardUnlockSelection = (v: string) => {
    if (!v) {
      setRewardUnlockQuizId('');
      setRewardUnlockPlayMedium(false);
      setRewardUnlockPlayHard(false);
      return;
    }
    if (v === REWARD_PLAY_MEDIUM) {
      setRewardUnlockQuizId('');
      setRewardUnlockPlayMedium(true);
      setRewardUnlockPlayHard(false);
      return;
    }
    if (v === REWARD_PLAY_BOTH) {
      setRewardUnlockQuizId('');
      setRewardUnlockPlayMedium(true);
      setRewardUnlockPlayHard(true);
      return;
    }
    if (v.startsWith('quiz:')) {
      setRewardUnlockQuizId(v.slice('quiz:'.length));
      setRewardUnlockPlayMedium(false);
      setRewardUnlockPlayHard(false);
    }
  };

  const resetForm = () => {
    setTitle('');
    setDescription('');
    setTitleEn('');
    setDescriptionEn('');
    setSourceQuizId('');
    setRewardUnlockQuizId('');
    setRewardXp(0);
    setRewardAchievementId('');
    setRewardUnlockPlayMedium(false);
    setRewardUnlockPlayHard(false);
    setIsActive(true);
    setSortOrder(0);
    setReqRows([emptyReqRow(0)]);
    setSourceQuestions([]);
    setEditing(null);
    setSpecificQuestionSearchByRow({});
    setGroupModalQuestionSearch('');
  };

  const openCreate = () => {
    resetForm();
    setShowCreate(true);
  };

  const openEdit = (m: CareerMissionAdmin) => {
    setEditing(m);
    setTitle(m.title);
    setDescription(m.description ?? '');
    setTitleEn(m.titleEn ?? '');
    setDescriptionEn(m.descriptionEn ?? '');
    setSourceQuizId(m.sourceQuizId);
    setRewardUnlockQuizId(m.rewardUnlockQuizId ?? '');
    setRewardXp(m.rewardXp);
    setRewardAchievementId(m.rewardAchievementId ?? '');
    let med = m.rewardUnlockPlayMedium === true;
    let hard = m.rewardUnlockPlayHard === true;
    // Antes existia opção “só Difícil”; política atual = desbloquear Médio+Difícil juntos.
    if (hard && !med && !(m.rewardUnlockQuizId ?? '')) {
      med = true;
    }
    setRewardUnlockPlayMedium(med);
    setRewardUnlockPlayHard(hard);
    setIsActive(m.isActive);
    setSortOrder(m.sortOrder);
    const rows = missionToRows(m);
    setReqRows(rows.length ? rows : [emptyReqRow(0)]);
    setShowEdit(true);
  };

  const buildPayloadRequirements = (includeRequirementIds: boolean): CareerMissionRequirementInput[] =>
    reqRows.map((r, idx) => {
      const base: CareerMissionRequirementInput = {
        sortOrder: idx,
        requirementType: r.requirementType,
        ...(includeRequirementIds && looksLikeRequirementGuid(r.clientId) ? { id: r.clientId } : {}),
      };
      if (
        r.requirementType === 'PERFECT_WINS_COUNT' ||
        r.requirementType === 'QUIZ_PLAY_COUNT'
      ) {
        const n = typeof r.targetCount === 'number' ? r.targetCount : Number(r.targetCount);
        return { ...base, targetCount: Number.isFinite(n) && n >= 1 ? n : 1 };
      }
      const withQuestions: CareerMissionRequirementInput = { ...base, questionIds: r.questionIds };
      if (isMcSourceQuiz) {
        const questionGroups: CareerMissionQuestionGroupInput[] = r.questionGroups.map((g) => ({
          label: g.label.trim(),
          questionIds: g.questionIds,
        }));
        return { ...withQuestions, questionGroups };
      }
      return withQuestions;
    });

  const validateCareerRequirementsForSubmit = (): string | null => {
    for (const row of reqRows) {
      if (row.requirementType !== 'SPECIFIC_QUESTIONS_CORRECT_ONCE') continue;
      if (row.questionIds.length === 0) {
        return t(
          'admin.careerSpecificNeedsQuestion',
          'Each “specific questions” requirement needs at least one question selected.'
        );
      }
      if (isMcSourceQuiz) {
        const assigned = new Set<string>();
        for (const g of row.questionGroups) {
          if (!g.label.trim()) {
            return t(
              'admin.careerGroupNeedsLabel',
              'Each question group needs a label (multiple-choice source quiz).'
            );
          }
          for (const id of g.questionIds) {
            if (!row.questionIds.includes(id)) {
              return t('admin.careerGroupInvalidQuestion', 'A group references a question that is not selected.');
            }
            if (assigned.has(id)) {
              return t(
                'admin.careerGroupDuplicateQuestion',
                'A question appears in more than one group. Remove or fix duplicates.'
              );
            }
            assigned.add(id);
          }
        }
        if (assigned.size !== row.questionIds.length) {
          return t(
            'admin.careerMcNeedsFullPartition',
            'For multiple-choice quizzes, assign every selected question to exactly one group before saving or activating the mission.'
          );
        }
      }
    }
    return null;
  };

  const handleCreate = async () => {
    if (!title.trim() || !titleEn.trim() || !sourceQuizId) {
      toast.error('Title (PT), title (EN), and source quiz are required');
      return;
    }
    const reqErr = validateCareerRequirementsForSubmit();
    if (reqErr) {
      toast.error(reqErr);
      return;
    }
    const body: CreateCareerMissionAdmin = {
      title: title.trim(),
      description: description.trim() || null,
      titleEn: titleEn.trim(),
      descriptionEn: descriptionEn.trim() || null,
      sourceQuizId,
      rewardUnlockQuizId: rewardUnlockQuizId || null,
      rewardXp,
      rewardAchievementId: rewardAchievementId || null,
      rewardUnlockPlayMedium,
      rewardUnlockPlayHard,
      isActive,
      sortOrder,
      requirements: buildPayloadRequirements(false),
    };
    try {
      await adminService.createCareerMission(body);
      toast.success('Career mission created');
      setShowCreate(false);
      resetForm();
      await loadAll();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      toast.error(msg || 'Create failed');
    }
  };

  const handleUpdate = async () => {
    if (!editing || !title.trim() || !titleEn.trim() || !sourceQuizId) {
      toast.error('Title (PT), title (EN), and source quiz are required');
      return;
    }
    const reqErr = validateCareerRequirementsForSubmit();
    if (reqErr) {
      toast.error(reqErr);
      return;
    }
    const body: UpdateCareerMissionAdmin = {
      title: title.trim(),
      description: description.trim() || null,
      titleEn: titleEn.trim(),
      descriptionEn: descriptionEn.trim() || null,
      sourceQuizId,
      rewardUnlockQuizId: rewardUnlockQuizId || null,
      rewardXp,
      rewardAchievementId: rewardAchievementId || null,
      rewardUnlockPlayMedium,
      rewardUnlockPlayHard,
      isActive,
      sortOrder,
      requirements: buildPayloadRequirements(true),
    };
    try {
      await adminService.updateCareerMission(editing.id, body);
      toast.success('Career mission updated');
      setShowEdit(false);
      resetForm();
      await loadAll();
    } catch (e: unknown) {
      const msg =
        e && typeof e === 'object' && 'response' in e
          ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
          : null;
      toast.error(msg || 'Update failed');
    }
  };

  const handleDelete = async (m: CareerMissionAdmin) => {
    if (!window.confirm(`Delete mission "${m.title}"?`)) return;
    try {
      await adminService.deleteCareerMission(m.id);
      toast.success('Deleted');
      await loadAll();
    } catch {
      toast.error('Delete failed');
    }
  };

  const updateReqRow = (clientId: string, patch: Partial<ReqRow>) => {
    setReqRows((rows) =>
      rows.map((r) => {
        if (r.clientId !== clientId) return r;
        const next = { ...r, ...patch } as ReqRow;
        if (patch.questionIds !== undefined && patch.questionGroups === undefined) {
          next.questionGroups = pruneGroupsForQuestionIds(r.questionGroups, patch.questionIds);
        }
        return next;
      })
    );
  };

  const addReqRow = () => {
    setReqRows((rows) => [...rows, emptyReqRow(rows.length)]);
  };

  const removeReqRow = (clientId: string) => {
    setReqRows((rows) => (rows.length <= 1 ? rows : rows.filter((r) => r.clientId !== clientId)));
  };

  const renderFormFields = (isEdit: boolean) => (
    <>
      <Form.Group className="mb-2">
        <Form.Label>Title (PT-BR)</Form.Label>
        <Form.Control value={title} onChange={(e) => setTitle(e.target.value)} maxLength={200} />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Title (English) — required</Form.Label>
        <Form.Control value={titleEn} onChange={(e) => setTitleEn(e.target.value)} maxLength={200} />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Description (PT-BR)</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
        />
      </Form.Group>
      <Form.Group className="mb-2">
        <Form.Label>Description (English)</Form.Label>
        <Form.Control
          as="textarea"
          rows={2}
          value={descriptionEn}
          onChange={(e) => setDescriptionEn(e.target.value)}
        />
      </Form.Group>
      <Row>
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label>Source quiz (progress)</Form.Label>
            <Form.Select
              value={sourceQuizId}
              onChange={(e) => setSourceQuizId(e.target.value)}
            >
              <option value="">— select —</option>
              {quizzes.map((q) => (
                <option key={q.id} value={q.id}>
                  {quizAdminDisplayLine(q)} ({q.language})
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={6}>
          <Form.Group className="mb-2">
            <Form.Label>
              {t('admin.careerUnlockRewardLabel', 'Unlock reward')}
            </Form.Label>
            <Form.Select
              value={rewardUnlockSelectValue()}
              onChange={(e) => applyRewardUnlockSelection(e.target.value)}
              disabled={!sourceQuizId}
            >
              <option value="">
                {t('admin.careerUnlockRewardNone', '— No unlock reward —')}
              </option>
              <optgroup
                label={t(
                  'admin.careerUnlockRewardSameQuizGroup',
                  'Same quiz as progress — Medium/Hard bands'
                )}
              >
                <option
                  value={REWARD_PLAY_MEDIUM}
                  disabled={!sourceQuiz || sourceQuiz.supportsPlayDifficultyMedium !== true}
                >
                  {sourceQuiz
                    ? `${quizAdminDisplayLine(sourceQuiz)} — ${t('quiz.medium', 'Medium')} (${t('admin.careerUnlockSoloMode', 'quiz')})`
                    : t('admin.careerPickSourceFirst', 'Select source quiz first')}
                </option>
                <option
                  value={REWARD_PLAY_BOTH}
                  disabled={
                    !sourceQuiz ||
                    sourceQuiz.supportsPlayDifficultyMedium !== true ||
                    sourceQuiz.supportsPlayDifficultyHard !== true
                  }
                >
                  {sourceQuiz
                    ? `${quizAdminDisplayLine(sourceQuiz)} — ${t('quiz.medium', 'Medium')} + ${t('quiz.hard', 'Hard')} (${t('admin.careerUnlockSoloMode', 'quiz')})`
                    : t('admin.careerPickSourceFirst', 'Select source quiz first')}
                </option>
              </optgroup>
              <optgroup
                label={t(
                  'admin.careerUnlockRewardOtherQuizGroup',
                  'Another quiz — full unlock (catalog)'
                )}
              >
                {quizzes
                  .filter((q) => q.isUnlockable || q.id === rewardUnlockQuizId)
                  .map((q) => (
                    <option key={`uq-${q.id}`} value={`quiz:${q.id}`}>
                      {quizAdminDisplayLine(q)} — {t('admin.careerUnlockFullQuizAccess', 'full quiz access')}
                      {q.language ? ` (${q.language})` : ''}
                    </option>
                  ))}
              </optgroup>
            </Form.Select>
            <Form.Text className="text-muted small d-block">
              {t(
                'admin.careerUnlockRewardHint',
                'Easy missions: use Medium + Hard to unlock both difficulty bands at once. Medium-only is for quizzes that only expose medium. “Full quiz access” requires unlockable quizzes in Quiz Management.'
              )}
            </Form.Text>
          </Form.Group>
        </Col>
      </Row>
      <Row>
        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label>Reward XP</Form.Label>
            <Form.Control
              type="number"
              min={0}
              value={rewardXp}
              onChange={(e) => setRewardXp(Number(e.target.value))}
            />
          </Form.Group>
        </Col>
        <Col md={4}>
          <Form.Group className="mb-2">
            <Form.Label>Reward achievement</Form.Label>
            <Form.Select
              value={rewardAchievementId}
              onChange={(e) => setRewardAchievementId(e.target.value)}
            >
              <option value="">— none —</option>
              {achievements.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.namePt} / {a.nameEn}
                </option>
              ))}
            </Form.Select>
          </Form.Group>
        </Col>
        <Col md={2}>
          <Form.Group className="mb-2">
            <Form.Label>Sort order</Form.Label>
            <Form.Control
              type="number"
              value={sortOrder}
              onChange={(e) => setSortOrder(Number(e.target.value))}
            />
          </Form.Group>
        </Col>
        <Col md={2} className="d-flex align-items-end pb-2">
          <Form.Check
            type="switch"
            id={`active-${isEdit ? 'e' : 'c'}`}
            label="Active"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
          />
        </Col>
      </Row>

      <hr />
      <div className="d-flex justify-content-between align-items-center mb-2">
        <strong>Requirements</strong>
        <Button size="sm" variant="outline-primary" type="button" onClick={addReqRow}>
          + Add requirement
        </Button>
      </div>
      {loadingQuestions && sourceQuizId ? (
        <Spinner animation="border" size="sm" className="me-2" />
      ) : null}

      {reqRows.map((row) => {
        const filteredSourceQuestions =
          row.requirementType === 'SPECIFIC_QUESTIONS_CORRECT_ONCE'
            ? filterCareerAdminQuestions(
                sourceQuestions,
                specificQuestionSearchByRow[row.clientId] ?? '',
                sourceQuiz
              )
            : [];
        return (
        <Card key={row.clientId} className="mb-2 p-2 bg-light">
          <Row className="g-2 align-items-end">
            <Col md={4}>
              <Form.Label className="small">Type</Form.Label>
              <Form.Select
                value={row.requirementType}
                onChange={(e) =>
                  updateReqRow(row.clientId, {
                    requirementType: e.target.value,
                    targetCount:
                      e.target.value === 'SPECIFIC_QUESTIONS_CORRECT_ONCE' ? '' : row.targetCount || 1,
                    questionIds:
                      e.target.value === 'SPECIFIC_QUESTIONS_CORRECT_ONCE' ? row.questionIds : [],
                    questionGroups:
                      e.target.value === 'SPECIFIC_QUESTIONS_CORRECT_ONCE' ? row.questionGroups : [],
                  })
                }
              >
                {REQ_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </Form.Select>
            </Col>
            {(row.requirementType === 'PERFECT_WINS_COUNT' ||
              row.requirementType === 'QUIZ_PLAY_COUNT') && (
              <Col md={3}>
                <Form.Label className="small">Target count</Form.Label>
                <Form.Control
                  type="number"
                  min={1}
                  value={row.targetCount === '' ? '' : row.targetCount}
                  onChange={(e) =>
                    updateReqRow(row.clientId, {
                      targetCount: e.target.value === '' ? '' : Number(e.target.value),
                    })
                  }
                />
              </Col>
            )}
            {row.requirementType === 'SPECIFIC_QUESTIONS_CORRECT_ONCE' && (
              <Col md={12}>
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-1">
                  <Form.Label className="small mb-0">Questions (source quiz)</Form.Label>
                  {sourceQuizId && sourceQuestions.length > 0 ? (
                    <div className="d-flex flex-wrap gap-2">
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="py-0"
                        onClick={() =>
                          updateReqRow(row.clientId, {
                            questionIds: Array.from(
                              new Set([
                                ...row.questionIds,
                                ...filteredSourceQuestions.map((q) => q.id),
                              ])
                            ),
                          })
                        }
                      >
                        {t('admin.careerSelectAllQuestions', 'Select all')}
                      </Button>
                      <Button
                        type="button"
                        variant="link"
                        size="sm"
                        className="py-0 text-secondary"
                        disabled={row.questionIds.length === 0}
                        onClick={() =>
                          updateReqRow(row.clientId, { questionIds: [], questionGroups: [] })
                        }
                      >
                        {t('admin.careerClearAllQuestions', 'Clear all')}
                      </Button>
                    </div>
                  ) : null}
                </div>
                {!sourceQuizId ? (
                  <p className="text-muted small mb-0">Select a source quiz first.</p>
                ) : (
                  <>
                    <Form.Group className="mb-2">
                      <Form.Control
                        type="search"
                        size="sm"
                        value={specificQuestionSearchByRow[row.clientId] ?? ''}
                        onChange={(e) =>
                          setSpecificQuestionSearchByRow((prev) => ({
                            ...prev,
                            [row.clientId]: e.target.value,
                          }))
                        }
                        placeholder={t(
                          'admin.careerQuestionSearchPlaceholder',
                          'Search by quiz title, theme, question text, or correct answer…'
                        )}
                        aria-label={t('admin.careerQuestionSearchLabel', 'Filter questions')}
                      />
                    </Form.Group>
                    <div
                      className="border rounded p-2"
                      style={{ maxHeight: 160, overflowY: 'auto' }}
                    >
                      {filteredSourceQuestions.length === 0 ? (
                        <p className="text-muted small mb-0">
                          {t(
                            'admin.careerQuestionSearchNoMatch',
                            'No questions match this filter.'
                          )}
                        </p>
                      ) : (
                        filteredSourceQuestions.map((q) => (
                          <Form.Check
                            key={q.id}
                            type="checkbox"
                            id={`${row.clientId}-q-${q.id}`}
                            label={sourceQuizQuestionCheckboxLabel(q)}
                            checked={row.questionIds.includes(q.id)}
                            onChange={(e) => {
                              const on = e.target.checked;
                              updateReqRow(row.clientId, {
                                questionIds: on
                                  ? [...row.questionIds, q.id]
                                  : row.questionIds.filter((id) => id !== q.id),
                              });
                            }}
                          />
                        ))
                      )}
                    </div>
                  </>
                )}
                {isMcSourceQuiz && sourceQuizId ? (
                  <div className="mt-3 border-top pt-3">
                    <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
                      <strong className="small">
                        {t('admin.careerQuestionGroupsTitle', 'Question groups (MC quiz)')}
                      </strong>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline-primary"
                        disabled={unassignedQuestionIds(row).length === 0}
                        onClick={() => {
                          setGroupModalReqClientId(row.clientId);
                          setGroupModalLabel('');
                          setGroupModalSelectedIds([]);
                          setGroupModalQuestionSearch('');
                        }}
                      >
                        {t('admin.careerAddQuestionGroup', 'Add grouping')}
                      </Button>
                    </div>
                    <p className="text-muted small mb-2">
                      {t(
                        'admin.careerQuestionGroupsHint',
                        'Every selected question must belong to exactly one named group before you can save or activate the mission.'
                      )}
                    </p>
                    {row.questionGroups.length > 0 ? (
                      <ul className="list-unstyled small mb-2">
                        {row.questionGroups.map((g, gi) => (
                          <li
                            key={`${row.clientId}-g-${gi}`}
                            className="d-flex flex-wrap justify-content-between gap-2 mb-1 border rounded px-2 py-1 bg-white"
                          >
                            <span className="me-2">
                              <strong>{g.label.trim() || '—'}</strong>
                              <span className="text-muted ms-2">
                                ({g.questionIds.length}{' '}
                                {t('admin.careerGroupQuestionsCount', 'questions')})
                              </span>
                            </span>
                            <Button
                              type="button"
                              variant="link"
                              size="sm"
                              className="py-0 text-danger flex-shrink-0"
                              onClick={() =>
                                setReqRows((rows) =>
                                  rows.map((r) =>
                                    r.clientId === row.clientId
                                      ? {
                                          ...r,
                                          questionGroups: r.questionGroups.filter((_, i) => i !== gi),
                                        }
                                      : r
                                  )
                                )
                              }
                            >
                              {t('admin.careerRemoveGroup', 'Remove group')}
                            </Button>
                          </li>
                        ))}
                      </ul>
                    ) : null}
                    {unassignedQuestionIds(row).length > 0 ? (
                      <Alert variant="warning" className="py-2 small mb-0">
                        {t('admin.careerUnassignedQuestionsPrefix', 'Still ungrouped:')}{' '}
                        {unassignedQuestionIds(row).length}{' '}
                        {t('admin.careerUnassignedQuestionsSuffix', 'question(s).')}
                      </Alert>
                    ) : row.questionIds.length > 0 ? (
                      <Alert variant="success" className="py-2 small mb-0">
                        {t(
                          'admin.careerAllQuestionsGrouped',
                          'All selected questions belong to a group.'
                        )}
                      </Alert>
                    ) : null}
                  </div>
                ) : null}
              </Col>
            )}
            <Col md="auto" className="ms-auto">
              <Button
                type="button"
                variant="outline-danger"
                size="sm"
                disabled={reqRows.length <= 1}
                onClick={() => removeReqRow(row.clientId)}
              >
                Remove
              </Button>
            </Col>
          </Row>
        </Card>
        );
      })}
    </>
  );

  return (
    <div className="admin-tab-content">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h3 className="mb-0">Career mode — missions</h3>
        <Button variant="primary" onClick={openCreate}>
          New mission
        </Button>
      </div>

      {error && <Alert variant="danger">{error}</Alert>}

      {loading ? (
        <div className="text-center py-4">
          <Spinner animation="border" />
        </div>
      ) : (
        <Card>
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Source</th>
                  <th>Unlock</th>
                  <th>XP</th>
                  <th>Reqs</th>
                  <th>Order</th>
                  <th>Active</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {items.map((m) => (
                  <tr key={m.id}>
                    <td>
                      <div>{m.title}</div>
                      {m.titleEn ? (
                        <div className="text-muted small">{m.titleEn}</div>
                      ) : null}
                    </td>
                    <td className="small">{m.sourceQuizTitle ?? m.sourceQuizId}</td>
                    <td className="small">{m.rewardUnlockQuizTitle ?? '—'}</td>
                    <td>{m.rewardXp}</td>
                    <td>{m.requirements.length}</td>
                    <td>{m.sortOrder}</td>
                    <td>
                      {m.isActive ? (
                        <Badge bg="success">Yes</Badge>
                      ) : (
                        <Badge bg="secondary">No</Badge>
                      )}
                    </td>
                    <td className="text-nowrap">
                      <Button size="sm" variant="outline-primary" className="me-1" onClick={() => openEdit(m)}>
                        Edit
                      </Button>
                      <Button size="sm" variant="outline-danger" onClick={() => void handleDelete(m)}>
                        Delete
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
            {missionsTotalCount === 0 && (
              <p className="text-muted text-center py-4 mb-0">No missions yet.</p>
            )}
            {missionsTotalPages > 1 && (
              <div className="d-flex justify-content-center py-3 border-top">
                <Pagination className="mb-0 flex-wrap justify-content-center">
                  <Pagination.First
                    disabled={!missionsHasPrev}
                    onClick={() => setMissionsPage(1)}
                  />
                  <Pagination.Prev
                    disabled={!missionsHasPrev}
                    onClick={() => setMissionsPage((p) => Math.max(1, p - 1))}
                  />
                  {Array.from({ length: Math.min(5, missionsTotalPages) }, (_, i) => {
                    const pageNum =
                      Math.max(1, Math.min(missionsTotalPages - 4, missionsPage - 2)) + i;
                    if (pageNum > missionsTotalPages) return null;
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === missionsPage}
                        onClick={() => setMissionsPage(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  })}
                  <Pagination.Next
                    disabled={!missionsHasNext}
                    onClick={() =>
                      setMissionsPage((p) => Math.min(missionsTotalPages, p + 1))
                    }
                  />
                  <Pagination.Last
                    disabled={!missionsHasNext}
                    onClick={() => setMissionsPage(missionsTotalPages)}
                  />
                </Pagination>
              </div>
            )}
          </Card.Body>
        </Card>
      )}

      <Modal show={showCreate} onHide={() => setShowCreate(false)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>New career mission</Modal.Title>
        </Modal.Header>
        <Modal.Body>{renderFormFields(false)}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCreate(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void handleCreate()}>
            Create
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showEdit} onHide={() => setShowEdit(false)} size="lg" scrollable>
        <Modal.Header closeButton>
          <Modal.Title>Edit career mission</Modal.Title>
        </Modal.Header>
        <Modal.Body>{renderFormFields(true)}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowEdit(false)}>
            Cancel
          </Button>
          <Button variant="primary" onClick={() => void handleUpdate()}>
            Save
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal
        show={!!groupModalReqClientId}
        onHide={() => {
          setGroupModalReqClientId(null);
          setGroupModalLabel('');
          setGroupModalSelectedIds([]);
          setGroupModalQuestionSearch('');
        }}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('admin.careerAddQuestionGroup', 'Add grouping')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>{t('admin.careerGroupLabel', 'Group name')}</Form.Label>
            <Form.Control
              value={groupModalLabel}
              onChange={(e) => setGroupModalLabel(e.target.value)}
              maxLength={200}
              placeholder={t('admin.careerGroupLabelPlaceholder', 'e.g. Graveler')}
            />
          </Form.Group>
          <Form.Label className="small">
            {t('admin.careerGroupPickQuestions', 'Questions to include in this group')}
          </Form.Label>
          <Form.Control
            type="search"
            size="sm"
            className="mb-2"
            value={groupModalQuestionSearch}
            onChange={(e) => setGroupModalQuestionSearch(e.target.value)}
            placeholder={t(
              'admin.careerGroupModalSearchPlaceholder',
              'Search by quiz title, theme, question, or correct answer…'
            )}
            aria-label={t('admin.careerQuestionSearchLabel', 'Filter questions')}
          />
          <div
            className="border rounded p-2 mt-1"
            style={{ maxHeight: 280, overflowY: 'auto' }}
          >
            {groupModalRow && groupModalReqClientId ? (
              groupModalUnassignedQuestions.length === 0 ? (
                <p className="text-muted small mb-0">
                  {unassignedQuestionIds(groupModalRow).length === 0
                    ? t('admin.careerGroupModalEmpty', 'No ungrouped questions.')
                    : t(
                        'admin.careerQuestionSearchNoMatch',
                        'No questions match this filter.'
                      )}
                </p>
              ) : (
                groupModalUnassignedQuestions.map((q) => (
                  <Form.Check
                    key={q.id}
                    type="checkbox"
                    id={`group-modal-q-${q.id}`}
                    label={sourceQuizQuestionCheckboxLabel(q)}
                    checked={groupModalSelectedIds.includes(q.id)}
                    onChange={(e) => {
                      const on = e.target.checked;
                      setGroupModalSelectedIds((prev) =>
                        on ? [...prev, q.id] : prev.filter((id) => id !== q.id)
                      );
                    }}
                  />
                ))
              )
            ) : (
              <p className="text-muted small mb-0">
                {t('admin.careerGroupModalEmpty', 'No ungrouped questions.')}
              </p>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => {
              setGroupModalReqClientId(null);
              setGroupModalLabel('');
              setGroupModalSelectedIds([]);
              setGroupModalQuestionSearch('');
            }}
          >
            {t('common.cancel', 'Cancel')}
          </Button>
          <Button variant="primary" type="button" onClick={() => commitGroupModal()}>
            {t('admin.careerGroupModalAdd', 'Add group')}
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default AdminCareerMissions;
